"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BiometricValidation = void 0;
const web_ui_framework_1 = require("@gruposantander/web-ui-framework");
const types_1 = require("./types");
const biometricValidation_helper_1 = require("./biometricValidation.helper");
class BiometricValidation {
    constructor({ verifyActions, xhr, agentToken, interactionId, urlConfig, getDeviceUUID, fetchIp }) {
        this.documentType = 'user';
        this.progressId = '';
        this.biometricId = '';
        /**
         * Set new verify actions. When you do a new assisted biometric validation, you need to update the state of the new component rendered, so you need to update the verify actions
         * @param verifyActions AssistedLoginVerifyActionsType
         */
        this.updateVerifyActions = (verifyActions) => {
            this.verifyActions = verifyActions;
        };
        /**
         * Initialize validation and wait for asyncConnection resolve
         * @param username string
         */
        this.waitUserAuthorization = (username) => __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.init(username);
                yield this.verifyInAppAuto();
            }
            catch (error) {
                this.verifyActions.onError({
                    code: types_1.AssistedLoginVerifyCodeType.ERROR,
                    subcode: types_1.AssistedLoginVerifySubCodeType.ERROR_INIT,
                    message: this.getResponseErrorMessage(error),
                    mode: types_1.AssistedLoginVerifyModeType.MANUAL,
                });
            }
        });
        /**
         * Check push status manually
         */
        this.manualVerify = () => __awaiter(this, void 0, void 0, function* () {
            yield this.verifyInAppManual();
        });
        this.init = (username) => __awaiter(this, void 0, void 0, function* () {
            this.deviceUUID = yield this.getDeviceUUID();
            const userIp = yield this.fetchIp(this.urlConfig.fetchIp);
            const resp = yield this.xhr.POST(this.urlConfig.init, {
                username,
                documentType: this.documentType,
                deviceInformation: {
                    deviceUUID: this.deviceUUID,
                    webDeviceInfo: { version: navigator.userAgent },
                },
                sessionId: this.interactionId,
            }, { headers: { accessToken: this.agentToken, ['True-Client-IP']: userIp } });
            this.progressId = resp.data.progressId;
            this.biometricId = resp.data.biometricId;
        });
        this.getVerifyCallData = () => {
            const dataToSend = {
                endpoint: this.urlConfig.getVerifyCall,
                body: {
                    biometricId: this.biometricId,
                },
                headers: {
                    progressId: this.progressId,
                    accessToken: this.agentToken,
                },
            };
            const verifyFinalActions = {
                onError: (err, callMode = types_1.AssistedLoginVerifyModeType.MANUAL) => this.verifyActions.onError(err !== null && err !== void 0 ? err : {
                    code: types_1.AssistedLoginVerifyCodeType.ERROR,
                    subcode: types_1.AssistedLoginVerifySubCodeType.ERROR_STATUS,
                    message: this.getResponseErrorMessage(),
                    mode: callMode,
                }),
                onSuccess: (pushStatus_1, ...args_1) => __awaiter(this, [pushStatus_1, ...args_1], void 0, function* (pushStatus, callMode = types_1.AssistedLoginVerifyModeType.MANUAL) {
                    var _a;
                    const DENIED_CODE_RESPONSE = 403;
                    try {
                        if (pushStatus === types_1.PushStatusType.SUCCESS) {
                            const response = yield this.verify();
                            this.verifyActions.onSuccess(response);
                            return true;
                        }
                    }
                    catch (err) {
                        const error = err;
                        if (error.httpStatus === DENIED_CODE_RESPONSE) {
                            this.verifyActions.onError({
                                code: types_1.AssistedLoginVerifyCodeType.DECLINED,
                                message: this.getResponseErrorMessage(),
                                mode: callMode,
                            });
                        }
                        else {
                            this.verifyActions.onError({
                                code: types_1.AssistedLoginVerifyCodeType.ERROR,
                                subcode: types_1.AssistedLoginVerifySubCodeType.ERROR_VERIFY,
                                message: this.getResponseErrorMessage(err),
                                mode: callMode,
                            });
                        }
                        (_a = this.asyncConnection) === null || _a === void 0 ? void 0 : _a.disconnect();
                    }
                    return false;
                }),
            };
            const apiCall = () => __awaiter(this, void 0, void 0, function* () {
                return this.xhr.POST(dataToSend.endpoint, dataToSend.body, {
                    headers: dataToSend.headers,
                });
            });
            return { verifyFinalActions, apiCall };
        };
        this.verifyInAppAuto = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            yield ((_a = this.asyncConnection) === null || _a === void 0 ? void 0 : _a.disconnect());
            const { verifyFinalActions, apiCall } = this.getVerifyCallData();
            this.asyncConnection = yield (0, biometricValidation_helper_1.getAsyncConnection)({ apiCall, verifyActions: verifyFinalActions });
        });
        this.verifyInAppManual = () => __awaiter(this, void 0, void 0, function* () {
            const { verifyFinalActions, apiCall } = this.getVerifyCallData();
            try {
                const response = yield apiCall();
                const isSuccess = yield verifyFinalActions.onSuccess(response.data.message);
                if (!isSuccess) {
                    verifyFinalActions.onError({
                        code: types_1.AssistedLoginVerifyCodeType.INCOMPLETE,
                        mode: types_1.AssistedLoginVerifyModeType.MANUAL,
                    });
                }
            }
            catch (error) {
                verifyFinalActions.onError();
            }
        });
        this.getRoleByResponseHeader = (responseHeaders) => {
            const xSecurityValue = responseHeaders[types_1.X_SECURITY_TOKEN];
            switch (xSecurityValue) {
                case types_1.VerifyResponseHeaderSecurity.PASSWORD_EXPIRED:
                    return web_ui_framework_1.ClientRole.EXPIRED_PASSWORD;
                case types_1.VerifyResponseHeaderSecurity.ONBOARDING:
                    return web_ui_framework_1.ClientRole.PROSPECT;
                default:
                    return web_ui_framework_1.ClientRole.CLIENT;
            }
        };
        this.verify = () => __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.xhr.POST(this.urlConfig.verify, {
                deviceInformation: {
                    deviceUUID: this.deviceUUID,
                    webDeviceInfo: {
                        version: navigator.userAgent,
                    },
                    sessionId: this.interactionId,
                },
            }, {
                headers: {
                    accessToken: this.agentToken,
                    progressId: this.progressId,
                },
            });
            const clientRole = this.getRoleByResponseHeader(resp.responseHeaders);
            return Object.assign(Object.assign({}, resp.data), { clientRole });
        });
        this.getResponseErrorMessage = (error) => {
            let message = '';
            if (error instanceof Error) {
                message = error.message;
            }
            return message || 'Error biometric validation';
        };
        this.xhr = xhr;
        this.verifyActions = verifyActions;
        this.agentToken = agentToken;
        this.interactionId = interactionId;
        this.urlConfig = urlConfig;
        this.getDeviceUUID = getDeviceUUID;
        this.fetchIp = fetchIp;
    }
}
exports.BiometricValidation = BiometricValidation;
//# sourceMappingURL=biometricValidation.js.map